// SPDX-License-Identifier: Apache-2.0

// postprocessing.ts — EffectComposer + RenderPass + EffectPass(Bloom, Vignette,
// GodRays). Port of v1 vault-orbit.tsx:1513-1538.
//
// v1 used `@react-three/postprocessing` (declarative wrapper). v2 uses the
// underlying `postprocessing` package directly — that's what the JSX wrapper
// constructs anyway, so the shape and tuning are identical.
//
// God rays target the brain core (instead of v1's standalone "SunNode" which
// we removed — the brain core IS the sun in v2's scene topology).

import * as THREE from "three";
import {
  EffectComposer,
  RenderPass,
  EffectPass,
  BloomEffect,
  VignetteEffect,
  GodRaysEffect,
  KernelSize,
  BlendFunction,
} from "postprocessing";

export interface OrbitPostFX {
  composer: EffectComposer;
  renderPass: RenderPass;
  bloom: BloomEffect;
  vignette: VignetteEffect;
  godRays: GodRaysEffect | null;
  effectPass: EffectPass;
  resize(w: number, h: number): void;
  dispose(): void;
}

export function createPostFX(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
  godRaysSource: THREE.Mesh | null,
): OrbitPostFX {
  const composer = new EffectComposer(renderer, {
    multisampling: 0,
    frameBufferType: THREE.HalfFloatType,
  });

  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  // Bloom — values match v1's <Bloom intensity 1.2-ish, threshold 0.4-ish>.
  // The brief asks for { intensity 1.2, luminanceThreshold 0.4 }; v1 actually
  // shipped with intensity 0.85 / threshold 0.24. We honour the brief but
  // keep mipmap blur for the soft halo v1 had.
  const bloom = new BloomEffect({
    intensity: 1.2,
    luminanceThreshold: 0.4,
    luminanceSmoothing: 0.85,
    mipmapBlur: true,
    radius: 0.7,
    levels: 8,
  });

  const vignette = new VignetteEffect({
    offset: 0.3,
    darkness: 0.7,
    blendFunction: BlendFunction.NORMAL,
  });

  let godRays: GodRaysEffect | null = null;
  if (godRaysSource) {
    godRays = new GodRaysEffect(camera, godRaysSource, {
      blendFunction: BlendFunction.SCREEN,
      samples: 30,
      density: 0.94,
      decay: 0.92,
      weight: 0.26,
      exposure: 0.19,
      clampMax: 0.85,
      kernelSize: KernelSize.SMALL,
      blur: true,
    });
  }

  const effectPass = godRays
    ? new EffectPass(camera, godRays, bloom, vignette)
    : new EffectPass(camera, bloom, vignette);
  composer.addPass(effectPass);

  return {
    composer,
    renderPass,
    bloom,
    vignette,
    godRays,
    effectPass,
    resize(w: number, h: number) {
      composer.setSize(w, h);
    },
    dispose() {
      composer.dispose();
      bloom.dispose();
      vignette.dispose();
      if (godRays) godRays.dispose();
    },
  };
}
